import { CommonModule } from '@angular/common';
import { Component, ContentChildren, Directive, EventEmitter, Input, Output, QueryList, TemplateRef } from '@angular/core';

export interface DataTableColumn {
  key: string;
  header: string;
  align?: 'start' | 'end';
}

export interface DataTableTextCell {
  type?: 'text';
  primary: string;
  secondary?: string;
  tone?: 'default' | 'muted' | 'strong';
  href?: string;
}

export interface DataTableBadgeCell {
  type: 'badge';
  label: string;
  tone?: 'success' | 'neutral' | 'warning' | 'danger';
}

export interface DataTableStatusCell {
  type: 'status';
  label: string;
  tone?: 'success' | 'neutral' | 'warning' | 'danger';
}

export interface DataTableAction {
  id: string;
  label: string;
  variant?: 'secondary' | 'primary';
  icon?: 'file' | 'download';
}

export interface DataTableActionsCell {
  type: 'actions';
  actions: DataTableAction[];
}

export type DataTableCell = string | DataTableTextCell | DataTableBadgeCell | DataTableStatusCell | DataTableActionsCell;

export interface DataTableRow {
  id: string | number;
  cells: Record<string, DataTableCell | DataTableCell[]>;
}

export interface DataTableFilter {
  id: string;
  label: string;
  icon?: 'calendar' | 'filter';
  options?: { label: string; value: string }[];
  value?: string;
}

@Directive({
  selector: 'ng-template[appDataTableCell]',
  standalone: true,
})
export class DataTableCellDirective {
  @Input('appDataTableCell') columnKey = '';

  constructor(public template: TemplateRef<{ $implicit: DataTableRow; column: DataTableColumn }>) {}
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
  ],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss',
})
export class DataTableComponent {
  @Input() label = 'Data table';
  @Input() columns: DataTableColumn[] = [];
  @Input() rows: DataTableRow[] = [];
  @Input() searchPlaceholder = '';
  @Input() searchValue = '';
  @Input() filters: DataTableFilter[] = [];
  @Input() page = 1;
  @Input() pageCount = 1;
  @Input() loading = false;
  @Input() emptyLabel = 'No results to show.';

  @Output() search = new EventEmitter<string>();
  @Output() filterActivate = new EventEmitter<string>();
  @Output() filterChange = new EventEmitter<{ id: string; value: string }>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() action = new EventEmitter<{ row: DataTableRow; actionId: string }>();

  @ContentChildren(DataTableCellDirective) cellTemplates!: QueryList<DataTableCellDirective>;

  get pages(): number[] {
    const total = Math.max(this.pageCount, 1);
    const windowSize = 5;
    if (total <= windowSize) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    let start = Math.max(1, this.page - 2);
    const end = Math.min(total, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  templateFor(key: string): TemplateRef<{ $implicit: DataTableRow; column: DataTableColumn }> | null {
    return this.cellTemplates?.find((template) => template.columnKey === key)?.template ?? null;
  }

  cellsFor(row: DataTableRow, column: DataTableColumn): DataTableCell[] {
    const value = row.cells[column.key];
    if (value == null) {
      return [];
    }
    return Array.isArray(value)
      ? value
      : [
          value,
        ];
  }

  isActionsLayout(cells: DataTableCell[]): boolean {
    return cells.length > 0 && cells.every((cell) => this.isActions(cell));
  }

  isText(cell: DataTableCell): cell is DataTableTextCell | string {
    return typeof cell === 'string' || cell.type === undefined || cell.type === 'text';
  }

  isBadge(cell: DataTableCell): cell is DataTableBadgeCell {
    return typeof cell !== 'string' && cell.type === 'badge';
  }

  isStatus(cell: DataTableCell): cell is DataTableStatusCell {
    return typeof cell !== 'string' && cell.type === 'status';
  }

  isActions(cell: DataTableCell): cell is DataTableActionsCell {
    return typeof cell !== 'string' && cell.type === 'actions';
  }

  textCell(cell: DataTableCell): DataTableTextCell {
    return typeof cell === 'string' ? { primary: cell } : (cell as DataTableTextCell);
  }

  trackFilter(_index: number, filter: DataTableFilter): string {
    return filter.id;
  }

  submitSearch(event: Event, input: HTMLInputElement): void {
    event.preventDefault();
    this.search.emit(input.value.trim());
  }

  onNativeSearch(event: Event, input: HTMLInputElement): void {
    if (input.value.trim() !== '') {
      return;
    }
    event.preventDefault();
    this.search.emit('');
  }

  onFilterChange(id: string, event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.filterChange.emit({ id, value });
  }

  goTo(page: number): void {
    if (page < 1 || page > Math.max(this.pageCount, 1) || page === this.page) {
      return;
    }
    this.pageChange.emit(page);
  }

  onAction(row: DataTableRow, actionId: string): void {
    this.action.emit({ row, actionId });
  }
}
